/**
 * Design tokens — CLAUDE.md §5.1: the entire storefront is styled through this
 * closed list of CSS variables. No component may hard-code a colour, font or
 * radius. Adding a token here is a deliberate act; it widens the surface every
 * template has to fill.
 */

export const TOKEN_KEYS = [
  'color-primary',
  'color-primary-contrast',
  'color-accent',
  'color-bg',
  'color-surface',
  'color-text',
  'color-text-muted',
  'color-border',
  'font-heading',
  'font-body',
  'radius',
  'radius-card',
  'shadow-card',
  'button-style',
  'header-style',
] as const;

export type TokenKey = (typeof TOKEN_KEYS)[number];

export type ThemeTokens = Partial<Record<TokenKey, string>>;

export function isTokenKey(key: string): key is TokenKey {
  return (TOKEN_KEYS as readonly string[]).includes(key);
}

/**
 * Fonts are a curated Khmer-safe list only (§5). Free font choice produces
 * storefronts where Khmer text renders as boxes, which we would rather never
 * ship than allow one merchant to pick Playfair Display.
 */
export interface FontPair {
  id: string;
  label: string;
  heading: string;
  body: string;
  /** Google Fonts family names to load, in the order the CSS `@import` needs. */
  families: string[];
}

export const FONT_PAIRS: readonly FontPair[] = [
  {
    id: 'kantumruy',
    label: 'Kantumruy Pro',
    heading: "'Kantumruy Pro', 'Noto Sans Khmer', system-ui, sans-serif",
    body: "'Kantumruy Pro', 'Noto Sans Khmer', system-ui, sans-serif",
    families: ['Kantumruy+Pro:wght@300;400;500;600;700'],
  },
  {
    id: 'noto',
    label: 'Noto Sans Khmer',
    heading: "'Noto Sans Khmer', system-ui, sans-serif",
    body: "'Noto Sans Khmer', system-ui, sans-serif",
    families: ['Noto+Sans+Khmer:wght@300;400;500;600;700'],
  },
  {
    id: 'moul-battambang',
    label: 'Moul + Battambang',
    heading: "'Moul', 'Noto Serif Khmer', serif",
    body: "'Battambang', 'Noto Sans Khmer', sans-serif",
    families: ['Moul', 'Battambang:wght@300;400;700'],
  },
  {
    id: 'battambang',
    label: 'Battambang',
    heading: "'Battambang', 'Noto Sans Khmer', sans-serif",
    body: "'Battambang', 'Noto Sans Khmer', sans-serif",
    families: ['Battambang:wght@300;400;700'],
  },
  {
    id: 'serif-khmer',
    label: 'Noto Serif Khmer',
    heading: "'Noto Serif Khmer', Georgia, serif",
    body: "'Noto Sans Khmer', system-ui, sans-serif",
    families: ['Noto+Serif+Khmer:wght@400;600;700', 'Noto+Sans+Khmer:wght@400;500;600'],
  },
];

export const FONT_PAIR_IDS = FONT_PAIRS.map((f) => f.id);

export function getFontPair(id: string): FontPair | undefined {
  return FONT_PAIRS.find((f) => f.id === id);
}

/** The values every storefront falls back to before a template is applied. */
export const DEFAULT_TOKENS: Required<ThemeTokens> = {
  'color-primary': '#111827',
  'color-primary-contrast': '#ffffff',
  'color-accent': '#f43f5e',
  'color-bg': '#ffffff',
  'color-surface': '#f8fafc',
  'color-text': '#0f172a',
  'color-text-muted': '#64748b',
  'color-border': '#e2e8f0',
  'font-heading': FONT_PAIRS[0]!.heading,
  'font-body': FONT_PAIRS[0]!.body,
  radius: '10px',
  'radius-card': '14px',
  'shadow-card': '0 1px 2px rgba(15, 23, 42, 0.06)',
  'button-style': 'solid',
  'header-style': 'plain',
};
